import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Typography, List, Button, ListItemButton, ListItemText, ListItemIcon, TextField, InputAdornment, CircularProgress, Popover, FormControl } from '@mui/material'
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DeleteIcon from '@mui/icons-material/Delete';
import ShareIcon from '@mui/icons-material/Share';
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import isToday from "dayjs/plugin/isToday";
import { createNewNote, getNotesByFolder, updateNote } from '../../../services/notes.service';
import { toast } from 'react-toastify';
import { cloneDeep } from 'lodash';
import DeletePopover from './DeleteNotePopup';
import SearchIcon from '@mui/icons-material/Search';
import _ from 'lodash';

dayjs.extend(relativeTime);
dayjs.extend(isToday);

const stripHtmlTags = (html: string, limit: number = 15): string => {
  if (!html) return "New Note";
  const doc = new DOMParser().parseFromString(html, "text/html");
  const text = doc.body.textContent || "";
  return text.length > limit ? text.substring(0, limit) : text;
};

interface NoteListTypes {
  userId: any;
  selectedFolderId: any | null;
  selectedNoteId: any | null;
  setSelectedNoteId: (noteId: string | null) => void;
  selectedNoteContent: any;
  setSelectedNoteContent: any;
  setIsContentLoading: (flag: boolean) => void;
  updatedNoteData: any;
  setUpdatedNoteData: (noteId: string | null) => void;
  setFromNewNote: (fromNewNote: boolean) => void;
}

const newNoteFields = {
  isNewNote: true,
  isEditTitle: false,
  title: "",
  content: ""
}

const NoteList: FC<NoteListTypes> = ({ userId, selectedFolderId, selectedNoteId, setSelectedNoteId, selectedNoteContent, setSelectedNoteContent, setIsContentLoading, updatedNoteData, setUpdatedNoteData, setFromNewNote }) => {
  const [noteList, setNoteList] = useState<any>([]);
  const [editableIndex, setEditableIndex] = useState<number | null>(null);
  const [isFetchingNotes, setIsFetchingNotes] = useState<boolean>(false);
  const [addNewNote, setAddNewNote] = useState<any>(null);
  const [selectedNote, setSelectedNote] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNewNoteSelected, setIsNewNoteSelected] = useState<boolean>(false);
  const saveNewNoteRef = useRef<any>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);
  const [deletableData, setDeletableData] = useState<any>(null);
  const [cloneNotes, setCloneNotes] = useState<any>([]);
  const [searchText, setSearchText] = useState<any>('');

  const handlePopoverClose = () => {
    requestAnimationFrame(() => {
      setAnchorEl(null);
    });
  };


  const fetchNotes = async () => {
    setIsFetchingNotes(true);
    setNoteList([]);
    setCloneNotes([]);
    setSelectedNoteId(null);
    setSelectedNoteContent("")
    setIsContentLoading(true);
    setIsNewNoteSelected(false)
    try {
      const notesData = await getNotesByFolder(selectedFolderId, userId);
      if (notesData?.statusCode === 200 && notesData?.data?.length > 0) {
        setNoteList([...notesData?.data])
        setCloneNotes([...notesData?.data])
        setSelectedNoteId(notesData?.data[0]?.id);
      } else {
        if (notesData?.data?.length === 0) {
          handleAddNewNotes()
        }
        setNoteList([]);
        setCloneNotes([]);
      }
    } catch (e) {
      console.log(e, "Error fetching notes");
    }
    setIsFetchingNotes(false);
    setIsContentLoading(false);
  }

  const handleSaveNewNote = async (fromEditor?: any) => {
    if (!saveNewNoteRef.current) return;
    setIsLoading(true);
    try {
      saveNewNoteRef.current = false;
      const payload = {
        userId,
        folderId: selectedFolderId,
        title: addNewNote?.title || "New Note",
        content: selectedNoteContent
      };
      if (fromEditor?.title) {
        payload.title = fromEditor?.title;
      }
      if (!payload?.title) return;

      const data = await createNewNote(payload);
      if (data?.statusCode === 200) {
        const notes = [...noteList];
        setNoteList(cloneDeep([data?.data, ...notes]));
        setCloneNotes(cloneDeep([data?.data, ...notes]));
        setFromNewNote(true)
        setSelectedNoteId(data?.data?.id);
        setAddNewNote(null);
        toast.success(data?.message);
      } else {
        if (data?.message !== "canceled") {
          toast.error(data?.message);
        }
      }
    } catch (e) {
      console.log(e, "error in note title saving")
    }
    setIsLoading(false);
    setAddNewNote(null);
  }

  const handleUpdateNotes = async () => {
    try {
      const payload = {
        userId,
        title: selectedNote?.title,
        content: selectedNoteContent
      };
      const data = await updateNote(selectedNote?.id, payload);
      if (data?.statusCode === 200) {
        const updatedNote = data?.data;
        const filterData = noteList.filter((ele: any) => ele?.id !== updatedNote?.id)
        setEditableIndex(null);
        setSelectedNote(null);
        setNoteList([updatedNote, ...filterData]);
        setCloneNotes([updatedNote, ...filterData]);
        toast.success(data?.message);
        document.getElementById("notes-container")?.scrollTo({
          top: 0,
          behavior: "smooth"
        });
      } else {
        if (data?.message !== "canceled") {
          toast.error(data?.message);
        }
      }
    } catch (e) {
      console.log(e, "error in update note")
    }
  }

  const handleAddNewNotes = () => {
    setSelectedNoteId("");
    setAddNewNote(newNoteFields);
    setSelectedNoteContent("");
    setIsNewNoteSelected(true);
    saveNewNoteRef.current = true;
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>, noteData: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget)
    setDeletableData(noteData);
  };

  const handleReset = () => {
    setSearchText('');
    setAddNewNote(null);
  }

  useEffect(() => {
    if (selectedFolderId) {
      fetchNotes()
      handleReset();
    } else {
      setNoteList([]);
      setCloneNotes([]);
      handleReset();
      setSelectedNoteId(null);
      setSelectedNote(null);
    }
  }, [selectedFolderId])

  useEffect(() => {
    if (addNewNote?.isNewNote && isNewNoteSelected && addNewNote?.isEditTitle) {
      setAddNewNote((prev: any) => ({ ...prev, isEditTitle: false }));
    }

    const timeoutId = setTimeout(() => {
      if (addNewNote?.isNewNote && selectedNoteContent?.length > 0 && isNewNoteSelected) {
        let newNoteObject = {
          title: stripHtmlTags(selectedNoteContent)
        }
        handleSaveNewNote(newNoteObject);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [selectedNoteContent]);

  const updatedNoteListFetch = async (selectedNoteId: any) => {
    try {
      const notesData = await getNotesByFolder(selectedFolderId, userId);
      if (notesData?.statusCode === 200 && notesData?.data?.length > 0) {
        let filterData: any = [];
        if (searchText?.length > 0) {
          const lowerQuery = searchText?.toLowerCase();
          filterData = notesData?.data?.filter((note: any) =>
            note.title.toLowerCase().includes(lowerQuery)
          );
        } else {
          filterData = notesData?.data
        }
        setTimeout(() => {
          setNoteList(cloneDeep(filterData));
          setCloneNotes(cloneDeep(notesData?.data));
        }, 100)
      } else {
        if (notesData?.data?.length === 0) {
          handleAddNewNotes()
        }
        setNoteList([]);
        setCloneNotes([]);
      }
    } catch (e) {
      console.log(e, "error update fetch")
    }
  }

  const handleSearchNote = useCallback(
    _.debounce((text: any) => {
      const lowerQuery = text?.toLowerCase();

      const filterData = cloneNotes?.filter((note: any) =>
        note.title.toLowerCase().includes(lowerQuery)
      );

      if (filterData?.length > 0) {
        setSelectedNoteId(filterData?.[0]?.id);
        setUpdatedNoteData(null);
        setIsNewNoteSelected(false);
        setEditableIndex(null);
      } else {
        setSelectedNoteId(null);
        setUpdatedNoteData(null);
        setIsNewNoteSelected(false);
        setEditableIndex(null);
        setSelectedNoteContent("");
      }

      setNoteList(filterData);
    }, 500),
    [cloneNotes]
  );

  useEffect(() => {
    if (updatedNoteData && selectedNoteId) {
      updatedNoteListFetch(selectedNoteId);
    }
  }, [updatedNoteData, selectedNoteId])

  return (
    <>
      <Typography variant="h6">Notes</Typography>
      <FormControl className="search-input">
        <TextField
          size="small"
          variant="outlined"
          placeholder='Search note...'
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target?.value)
            handleSearchNote(e.target?.value)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </FormControl>
      <List id="notes-container" className="list-Container">
        {addNewNote && addNewNote?.isNewNote && (
          <>
            {!addNewNote?.isEditTitle ? (
              <ListItemButton
                selected={isNewNoteSelected}
                onClick={() => {
                  setIsNewNoteSelected(true)
                  setSelectedNoteId(null);
                  setSelectedNoteContent("")
                  setEditableIndex(null);
                }}
                onDoubleClick={() => {
                  setAddNewNote((prev: any) => ({
                    ...prev, isEditTitle: true
                  }))
                  setEditableIndex(null);
                }}
              >
                <ListItemIcon>
                  <StickyNote2Icon />
                </ListItemIcon>
                <ListItemText
                  primary={addNewNote?.title || isNewNoteSelected ? stripHtmlTags(selectedNoteContent) : "New Note"}
                  secondary={dayjs().fromNow()}
                />
              </ListItemButton>
            ) : (
              <TextField
                id="standard-basic"
                placeholder='Add new title'
                variant="outlined"
                sx={{ mt: 2, width: "100%" }}
                value={addNewNote?.title}
                onChange={(e) => {
                  setAddNewNote((prev: any) => ({
                    ...prev, title: e?.target?.value
                  }))
                }}
                onKeyDown={(key) => {
                  if (["NumpadEnter", "Enter"].includes(key.code)) {
                    handleSaveNewNote();
                  }
                }}
                helperText="Press Enter to save your changes."
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      position="end"
                      className='cursor-pointer'
                      onClick={() => {
                        setAddNewNote((prev: any) => ({
                          ...prev, isEditTitle: false
                        }))
                      }}
                    >
                      {isLoading && <CircularProgress size={20} color="inherit" />}
                      <CloseIcon />
                    </InputAdornment>
                  )
                }}
              />)
            }
          </>
        )}
        {noteList?.length > 0 ? (
          noteList.map((note: any, index: number) => (
            editableIndex !== index ? (
              <ListItemButton
                selected={selectedNoteId === note?.id}
                key={note?.id}
                onClick={() => {
                  setSelectedNoteId(note?.id);
                  setUpdatedNoteData(null)
                  setIsNewNoteSelected(false)
                  setEditableIndex(null);
                  if (addNewNote?.isNewNote) {
                    setAddNewNote((prev: any) => ({ ...prev, isEditTitle: false }));
                  }
                  if (selectedNoteId !== note?.id) {
                    setSelectedNoteContent("");
                  }
                }}
                onDoubleClick={(e: any) => {
                  e?.stopPropagation();
                  setEditableIndex(index);
                  setSelectedNote(note);
                  if (addNewNote?.isNewNote) {
                    setAddNewNote((prev: any) => ({ ...prev, isEditTitle: false }));
                  }
                }}
              >
                <ListItemIcon>
                  <StickyNote2Icon />
                </ListItemIcon>
                <ListItemText
                  primary={note?.title}
                  secondary={dayjs(note?.updatedAt).fromNow()}
                />
                <div style={{ padding: "1rem 0" }} onClick={(e: any) => handleClick(e, note)}>
                  <MoreHorizIcon />
                </div>
              </ListItemButton>
            ) : (
              <TextField
                key={note?.id}
                id="standard-basic"
                placeholder='add new note'
                variant="outlined"
                sx={{ mt: 2, width: "100%" }}
                value={selectedNote?.title}
                onChange={(e) => {
                  setSelectedNote((prev: any) => ({
                    ...prev, title: e?.target.value
                  }))
                }}
                onKeyDown={(key) => {
                  if (["NumpadEnter", "Enter"].includes(key.code)) {
                    handleUpdateNotes();
                  }
                }}
                helperText="Press Enter to save your changes."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" className='cursor-pointer' onClick={() => {
                      setEditableIndex(null)
                      setSelectedNote(null);
                    }} >
                      <CloseIcon />
                    </InputAdornment>
                  )
                }}
              />
            )
          ))
        ) : (
          <>
            {!isFetchingNotes && !addNewNote?.isNewNote ? <ListItemButton className='position-center'>
              <ListItemText primary={"No notes found"} />
            </ListItemButton> : null}
          </>
        )}
        {(isFetchingNotes) && (
          <ListItemButton className='position-center' sx={{ display: "flex", flexDirection: "column", gap: 1 }} >
            <CircularProgress size={20} />
            <ListItemText className="fade-text" primary="Loading..." />
          </ListItemButton>)
        }
      </List>
      {selectedFolderId && (
        <footer>
          <Button variant="contained" onClick={handleAddNewNotes} >
            Create note
          </Button>
        </footer>
      )}
      {anchorEl && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          className="notification-more-popover"
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <div className="list-options" onClick={() => setIsDeleteOpen(true)}>
            <DeleteIcon />
            <Typography>Delete</Typography>
          </div>
          <div className="list-options disabled" >
            <ShareIcon />
            <Typography>Share with</Typography>
          </div>
        </Popover>
      )}
      {isDeleteOpen && (
        <DeletePopover
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
            setDeletableData(null);
            handlePopoverClose()
            fetchNotes();
          }}
          entityType="note"
          deletableData={deletableData}
        />
      )}
    </>
  )
}

export default NoteList